import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { FooterLogo } from "@/components/Logo";
import { COMPANY, NAV_ITEMS, SERVICES } from "@/lib/content";

// Architectural footer — seamlessly continues the dark field of the CTA above.
// No boxed logo card; the brand identity sits as a deliberate plaque within
// a 12-column composition with generous spacing and calm rhythm.
const Footer = () => {
  return (
    <footer
      className="relative bg-abayle-green-darker text-white"
      data-testid="site-footer"
    >
      {/* Ambient gold wash, very subtle, ties into the CTA bleed */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 90% 10%, #C9A13B 0, transparent 45%)",
        }}
      />

      {/* Main footer grid */}
      <div className="relative container-abayle pt-16 sm:pt-20 lg:pt-24 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 lg:gap-12">
          {/* Brand column */}
          <div className="md:col-span-12 lg:col-span-5">
            {/* Monochrome white logo sits directly on the green footer — no plate, no card */}
            <div className="flex items-center gap-5">
              <FooterLogo />
              <div className="leading-tight">
                <div className="text-white font-bold tracking-tight text-xl sm:text-2xl">
                  ABAYLE
                </div>
                <div className="text-abayle-gold text-[10px] sm:text-[11px] font-semibold tracking-[0.22em] uppercase mt-1">
                  Logistics &amp; Trading
                </div>
              </div>
            </div>

            <p className="mt-8 text-white/70 leading-[1.9] text-sm sm:text-[15px] max-w-md">
              A trusted Somali partner built for movement, trade, and client confidence — delivering
              integrated logistics, global trade, clearance, car sales, and car rental services
              across Somalia.
            </p>

            <p className="mt-8 text-abayle-gold font-semibold tracking-[0.25em] uppercase text-[11px]">
              {COMPANY.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-4 lg:col-span-2">
            <h4 className="text-abayle-gold text-[11px] font-semibold tracking-[0.25em] uppercase mb-6">
              Navigate
            </h4>
            <ul className="space-y-3.5">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-white/75 hover:text-abayle-gold transition-colors text-[14px]"
                    data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-4 lg:col-span-2">
            <h4 className="text-abayle-gold text-[11px] font-semibold tracking-[0.25em] uppercase mb-6">
              Services
            </h4>
            <ul className="space-y-3.5">
              {SERVICES.map((s) => (
                <li key={s.key}>
                  <Link
                    to={`/services#${s.key}`}
                    className="text-white/75 hover:text-abayle-gold transition-colors text-[14px]"
                    data-testid={`footer-service-${s.key}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4 lg:col-span-3">
            <h4 className="text-abayle-gold text-[11px] font-semibold tracking-[0.25em] uppercase mb-6">
              Contact
            </h4>
            <ul className="space-y-4 text-[14px]">
              {COMPANY.phones.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <Phone className="h-4 w-4 text-abayle-gold mt-[3px] shrink-0" />
                  <a
                    href={`tel:${p.replace(/\s+/g, "")}`}
                    className="text-white/80 hover:text-abayle-gold transition-colors"
                    data-testid={`footer-phone-${p}`}
                  >
                    {p}
                  </a>
                </li>
              ))}
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-abayle-gold mt-[3px] shrink-0" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-white/80 hover:text-abayle-gold transition-colors break-all"
                  data-testid="footer-email"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-abayle-gold mt-[3px] shrink-0" />
                <span className="text-white/80 leading-relaxed">{COMPANY.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Delicate divider — integrated, not heavy */}
        <div className="mt-14 lg:mt-20 border-t border-white/10" />

        {/* Bottom bar — integrated into the same field, not detached */}
        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[12px] text-white/55 tracking-wide flex flex-col sm:flex-row sm:items-center gap-x-5 gap-y-2">
            <span>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</span>
            <Link
              to="/cookies-policy"
              className="text-white/70 hover:text-abayle-gold transition-colors"
              data-testid="footer-cookies-policy"
            >
              Cookies Policy
            </Link>
          </div>
          <div className="text-[10px] sm:text-[11px] text-white/55 tracking-[0.28em] uppercase">
            Logistics <span className="text-abayle-gold/70 mx-1.5">•</span>
            Global Trade <span className="text-abayle-gold/70 mx-1.5">•</span>
            Clearance <span className="text-abayle-gold/70 mx-1.5">•</span>
            Car Sales <span className="text-abayle-gold/70 mx-1.5">•</span>
            Car Rental
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
